import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3, TrendingUp, Users, Eye, MousePointerClick, UserPlus,
  Activity, Loader2, AlertCircle, Calendar,
} from 'lucide-react';

export default function Analytics() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';

  const isMaster = user?.role === 'super_admin';
  // Master sees the whole platform; non-masters see only their company
  const scope = isMaster ? 'platform' : 'company';

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiClient.get(`/api/analytics/summary?days=${days}&scope=${scope}`)
      .then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(e.response?.data?.detail || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [days, isAdmin, scope]);

  if (!isAdmin) {
    return (
      <div className="space-y-4 fade-in">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-sky-500" /> Analytics
        </h1>
        <Card>
          <CardContent className="p-6 flex items-start gap-3 text-amber-300">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Admin-only</p>
              <p className="text-sm text-muted-foreground">Only company admins can view analytics.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (err || !data) {
    return <Card><CardContent className="p-6 text-rose-400">{err || 'No data'}</CardContent></Card>;
  }

  const totals = data.totals || {};
  const series = data.daily_series || [];
  const maxVisitors = Math.max(1, ...series.map((d) => d.visitors));
  const maxPageviews = Math.max(1, ...series.map((d) => d.pageviews));
  const funnelMax = Math.max(1, ...data.funnel.map((f) => f.count));

  return (
    <div className="space-y-6 fade-in" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 mb-1">
            <BarChart3 className="w-7 h-7 text-sky-500" /> Analytics
          </h1>
          <p className="text-muted-foreground">Site visits, in-app activity, and conversion funnel.</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="analytics-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Users}      color="blue"   label="Unique Visitors" value={totals.unique_visitors} sub={`${totals.new_visitors} new`} testid="kpi-visitors" />
        <Kpi icon={Eye}        color="purple" label="Page Views"      value={totals.pageviews}      sub={`${totals.sessions} sessions`} testid="kpi-pageviews" />
        <Kpi icon={Activity}   color="green"  label="DAU / WAU"       value={`${totals.dau} / ${totals.wau}`} sub="Daily / Weekly active" testid="kpi-dauwau" />
        <Kpi icon={MousePointerClick} color="amber" label="Tracked Actions" value={totals.events} sub="In-app events" testid="kpi-events" />
      </div>

      {/* Daily trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-sky-500" /> Daily Traffic</CardTitle>
          <CardDescription>Visitors vs pageviews per day</CardDescription>
        </CardHeader>
        <CardContent>
          {series.every((d) => d.visitors === 0 && d.pageviews === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-8">No traffic yet for this period.</p>
          ) : (
            <div className="space-y-1">
              {series.map((d) => (
                <div key={d.date} className="flex items-center gap-3 text-xs">
                  <span className="w-20 text-muted-foreground">{d.date.slice(5)}</span>
                  <div className="flex-1 flex items-center gap-1">
                    <div className="flex-1 bg-muted rounded h-4 overflow-hidden">
                      <div className="h-full bg-sky-500" style={{ width: `${(d.visitors / maxVisitors) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right text-sky-400 font-mono">{d.visitors}</span>
                    <div className="flex-1 bg-muted rounded h-4 overflow-hidden ml-2">
                      <div className="h-full bg-purple-500" style={{ width: `${(d.pageviews / maxPageviews) * 100}%` }} />
                    </div>
                    <span className="w-10 text-right text-purple-400 font-mono">{d.pageviews}</span>
                  </div>
                </div>
              ))}
              <div className="flex gap-4 pt-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-sky-500 rounded-sm" /> Visitors</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded-sm" /> Pageviews</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top pages */}
        <Card>
          <CardHeader>
            <CardTitle>Top Pages</CardTitle>
            <CardDescription>Most-visited routes</CardDescription>
          </CardHeader>
          <CardContent>
            {(!data.top_pages || data.top_pages.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-6">No pageviews yet.</p>
            ) : (
              <div className="space-y-2">
                {data.top_pages.map((p) => (
                  <div key={p.path} className="flex items-center justify-between text-sm">
                    <span className="truncate font-mono text-xs">{p.path}</span>
                    <span className="font-semibold text-sky-400">{p.views}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top events */}
        <Card>
          <CardHeader>
            <CardTitle>Top In-App Actions</CardTitle>
            <CardDescription>Most-fired tracked events</CardDescription>
          </CardHeader>
          <CardContent>
            {(!data.top_events || data.top_events.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-6">No tracked actions yet.</p>
            ) : (
              <div className="space-y-2">
                {data.top_events.map((e) => (
                  <div key={e.event} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs">{e.event}</span>
                    <span className="font-semibold text-emerald-400">{e.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Conversion funnel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-500" /> Conversion Funnel</CardTitle>
          <CardDescription>From first visit to paid invoice</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {data.funnel.map((f, i) => {
              const width = (f.count / funnelMax) * 100;
              const drop = i > 0 ? data.funnel[i - 1].count - f.count : 0;
              const dropPct = i > 0 && data.funnel[i - 1].count > 0
                ? Math.round((drop / data.funnel[i - 1].count) * 100) : 0;
              return (
                <div key={f.step}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{f.step}</span>
                    <span className="font-semibold">{f.count}{i > 0 && drop > 0 && <span className="text-rose-400 text-xs ml-2">−{dropPct}%</span>}</span>
                  </div>
                  <div className="w-full bg-muted rounded h-3 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500" style={{ width: `${width}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const COLOR_MAP = {
  blue:   { card: 'stat-blue',   icon: 'text-blue-500' },
  purple: { card: 'stat-purple', icon: 'text-purple-500' },
  green:  { card: 'stat-green',  icon: 'text-green-500' },
  amber:  { card: 'stat-orange', icon: 'text-amber-500' },
};

function Kpi({ icon: Icon, color, label, value, sub, testid }) {
  const c = COLOR_MAP[color];
  return (
    <Card className={`${c.card} border`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className={`w-7 h-7 ${c.icon}`} />
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}
